import { useCallback, useEffect, useRef, useState } from 'react';
import { post } from '@aiptyg/api-client';
import type { CaptchaSubmission } from '../types/auth.types';

type Point = { x: number; y: number };
type Challenge = { id: string; image: string; hint_image: string; width: number; height: number; count: number; expires_in: number };

export function SelfHostedLoginCaptcha({ email, onVerified, onClose }: {
  email: string;
  onVerified: (submission: CaptchaSubmission) => void;
  onClose: () => void;
}) {
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const sequence = useRef(0);
  const submitted = useRef(false);
  const expiresAt = useRef(0);
  const [deviceKey] = useState(() => {
    const key = 'captcha-device-key';
    try {
      const current = localStorage.getItem(key);
      if (current) return current;
      const created = crypto.randomUUID();
      localStorage.setItem(key, created);
      return created;
    } catch { return crypto.randomUUID(); }
  });
  const reload = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true); setChallenge(null); setPoints([]); setError(''); submitted.current = false;
    try {
      const result = await post<Challenge>('/v1/public/captcha', { scene: 'login', email, device_key: deviceKey });
      if (request !== sequence.current) return;
      if (!result.id || !result.image?.startsWith('data:image/') || !result.hint_image?.startsWith('data:image/') ||
          result.width !== 300 || result.height !== 180 || result.count !== 3 || result.expires_in <= 0) throw new Error('invalid challenge');
      expiresAt.current = Date.now() + result.expires_in * 1000;
      setChallenge(result);
    } catch (e) {
      if (request === sequence.current) setError((e as { status?: number }).status === 429 ? '操作过于频繁，请稍后重试' : '验证码加载失败，请重试');
    } finally { if (request === sequence.current) setLoading(false); }
  }, [deviceKey, email]);
  useEffect(() => { void reload(); return () => { sequence.current++; }; }, [reload]);
  useEffect(() => {
    if (!challenge) return;
    const timer = setTimeout(() => setError('验证码已过期，请换一张'), Math.max(0, expiresAt.current - Date.now()));
    return () => clearTimeout(timer);
  }, [challenge]);
  const confirm = () => {
    if (!challenge || error || submitted.current || points.length !== challenge.count) return;
    if (Date.now() >= expiresAt.current) { setError('验证码已过期，请换一张'); return; }
    submitted.current = true;
    onVerified({ captchaDeviceKey: deviceKey, captchaVerifyParam: JSON.stringify({ id: challenge.id, points }) });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="人机验证">
    <div className="w-full max-w-sm rounded-lg bg-white p-5 space-y-4">
      <div className="flex justify-between items-center"><h2 className="font-semibold">人机验证</h2><button type="button" onClick={onClose}>关闭</button></div>
      {loading && <p role="status">正在加载验证码…</p>}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {challenge && !error && <>
        <div className="flex items-center gap-2 text-sm">请按顺序点击图中字符<img src={challenge.hint_image} alt="待点击的字符顺序" className="h-9 max-w-32 object-contain" onError={() => setError('验证码加载失败，请重试')} /></div>
        <div className="relative w-full select-none" style={{ aspectRatio: '300/180' }} onClick={(event) => {
          if (submitted.current || Date.now() >= expiresAt.current) { setError('验证码已过期，请换一张'); return; }
          const rect = event.currentTarget.getBoundingClientRect();
          const point = { x: Math.max(0, Math.min(299, Math.round((event.clientX - rect.left) * 300 / rect.width))), y: Math.max(0, Math.min(179, Math.round((event.clientY - rect.top) * 180 / rect.height))) };
          setPoints((old) => old.length < challenge.count ? [...old, point] : old);
        }}>
          <img src={challenge.image} alt="点选验证码" draggable={false} className="pointer-events-none w-full h-full" onError={() => setError('验证码加载失败，请重试')} />
          {points.map((point, i) => <span key={i} className="pointer-events-none absolute flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white text-xs -translate-x-1/2 -translate-y-1/2" style={{ left: `${point.x / 3}%`, top: `${point.y / 1.8}%` }}>{i + 1}</span>)}
        </div>
      </>}
      <div className="flex items-center justify-between text-sm">
        <button type="button" disabled={loading} onClick={() => void reload()}>换一张</button>
        <button type="button" onClick={() => setPoints([])}>重选</button>
        <button type="button" disabled={loading || !!error || points.length !== 3} onClick={confirm} className="rounded bg-primary px-4 py-2 text-white disabled:opacity-50">确认 {points.length}/3</button>
      </div>
    </div>
  </div>;
}
